'use client'

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaRegSmile, 
  FaRegFrown, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaBook,
  FaLightbulb,
  FaLeaf
} from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const FoodTrackerCalculator = () => {
  const [inputs, setInputs] = useState({
    mealType: '',
    foodItem: '',
    portionSize: '',
    timestamp: new Date().toISOString()
  });
  const [results, setResults] = useState([]);
  const [showGraph, setShowGraph] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addToTracker = () => {
    if (!inputs.mealType || !inputs.foodItem || !inputs.portionSize) return;

    const newResult = {
      ...inputs,
      id: Date.now()
    };

    setResults(prev => [newResult, ...prev]);
    setShowGraph(true);
  };

  const barData = {
    labels: ['Breakfast', 'Lunch', 'Dinner', 'Snacks'],
    datasets: [
      {
        label: 'Meal Count',
        data: [
          results.filter(result => result.mealType === 'breakfast').length,
          results.filter(result => result.mealType === 'lunch').length,
          results.filter(result => result.mealType === 'dinner').length,
          results.filter(result => result.mealType === 'snack').length
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(153, 102, 255, 0.8)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Meal Distribution',
        color: '#2c3e50',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed) {
              label += context.parsed + ' meals';
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          }
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  return (
    <div>
      <Helmet>
        <title>Food Tracker Calculator - Track Your Daily Food Intake</title>
        <meta name="description" content="Track your daily food intake, portion sizes, and meal patterns. Monitor your eating habits and nutritional goals with our food tracker calculator." />
        <meta property="og:title" content="Food Tracker Calculator" />
        <meta property="og:description" content="Track your daily food intake, portion sizes, and meal patterns. Monitor your eating habits and nutritional goals with our food tracker calculator." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/images/food-tracker-calculator.jpg" />
        <meta name="keywords" content="food tracker, meal tracking, portion control, nutrition calculator, daily food intake, eating habits" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">
            🍽️ Food Tracker Calculator
          </h1>
          <p className="text-gray-600">
            Track your daily food intake and nutritional goals
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Type
            </label>
            <select
              name="mealType"
              value={inputs.mealType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select a meal type</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Food Item
            </label>
            <input
              type="text"
              name="foodItem"
              value={inputs.foodItem}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter food item name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Portion Size
            </label>
            <input
              type="number"
              name="portionSize"
              value={inputs.portionSize}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter portion size"
            />
          </div>
        </div>

        <button
          onClick={addToTracker}
          className="w-full bg-gradient-to-r from-green-500 to-green-600 text-yellow py-2 px-4 rounded-md hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-green-500/50"
          disabled={!inputs.mealType || !inputs.foodItem || !inputs.portionSize}
        >
          Add to Tracker
        </button>

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Recent Food Entries</h2>
            <div className="space-y-4">
              {results.map((result, index) => (
                <div key={result.id} className="p-4 bg-green-50 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-lg">{result.foodItem}</h3>
                      <p className="text-sm text-gray-600">{new Date(result.timestamp).toLocaleString()}</p>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium text-center min-w-[80px] ${
                      result.mealType === 'breakfast' ? 'bg-green-100 text-green-800' :
                      result.mealType === 'lunch' ? 'bg-yellow-100 text-yellow-800' :
                      result.mealType === 'dinner' ? 'bg-red-100 text-red-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {result.mealType.charAt(0).toUpperCase() + result.mealType.slice(1)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <p className="text-gray-600 font-medium mb-1">Portion Size:</p>
                    <p className="text-gray-700">{result.portionSize} units</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Meal Distribution</h2>
            <div className="h-[300px] lg:h-[350px] bg-white rounded-lg shadow-lg p-4">
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-full">
                  <Bar 
                    data={barData} 
                    options={barOptions}
                  />
                </div>
                <div className="mt-3 text-center text-gray-600 w-full">
                  <div className="flex justify-center space-x-1.5">
                    <div className="flex items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400 mr-1.5"></span>
                      <span className="text-green-600 text-sm">{results.filter(r => r.mealType === 'breakfast').length}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 mr-1.5"></span>
                      <span className="text-yellow-600 text-sm">{results.filter(r => r.mealType === 'lunch').length}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400 mr-1.5"></span>
                      <span className="text-red-600 text-sm">{results.filter(r => r.mealType === 'dinner').length}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-purple-400 mr-1.5"></span>
                      <span className="text-purple-600 text-sm">{results.filter(r => r.mealType === 'snack').length}</span>
                    </div>
                  </div>
                  <p className="text-sm font-medium mt-1.5">Total Meals: {results.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Diet Analysis & Recommendations</h2>
            <div className="space-y-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Meal Frequency Analysis</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                    <p className="flex-1">Breakfast: {Math.round((results.filter(r => r.mealType === 'breakfast').length / results.length) * 100)}% of meals</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-yellow-400 mr-2"></span>
                    <p className="flex-1">Lunch: {Math.round((results.filter(r => r.mealType === 'lunch').length / results.length) * 100)}% of meals</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-red-400 mr-2"></span>
                    <p className="flex-1">Dinner: {Math.round((results.filter(r => r.mealType === 'dinner').length / results.length) * 100)}% of meals</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-purple-400 mr-2"></span>
                    <p className="flex-1">Snacks: {Math.round((results.filter(r => r.mealType === 'snack').length / results.length) * 100)}% of meals</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Portion Size Analysis</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                    <p className="flex-1">Average Portion Size: {Math.round(results.reduce((acc, r) => acc + parseInt(r.portionSize), 0) / results.length)} units</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-yellow-400 mr-2"></span>
                    <p className="flex-1">Largest Portion: {Math.max(...results.map(r => parseInt(r.portionSize)))} units</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-red-400 mr-2"></span>
                    <p className="flex-1">Smallest Portion: {Math.min(...results.map(r => parseInt(r.portionSize)))} units</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Eating Pattern Analysis</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                    <p className="flex-1">Most Active Eating Period: {Object.entries(
                      results.reduce((acc, r) => {
                        const hour = new Date(r.timestamp).getHours();
                        acc[hour] = (acc[hour] || 0) + 1;
                        return acc;
                      }, {})
                    )
                    .sort((a, b) => b[1] - a[1])[0][0]}:00 - {Object.entries(
                      results.reduce((acc, r) => {
                        const hour = new Date(r.timestamp).getHours();
                        acc[hour] = (acc[hour] || 0) + 1;
                        return acc;
                      }, {})
                    )
                    .sort((a, b) => b[1] - a[1])[0][0] + 1}:00</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-yellow-400 mr-2"></span>
                    <p className="flex-1">Least Active Eating Period: {Object.entries(
                      results.reduce((acc, r) => {
                        const hour = new Date(r.timestamp).getHours();
                        acc[hour] = (acc[hour] || 0) + 1;
                        return acc;
                      }, {})
                    )
                    .sort((a, b) => a[1] - b[1])[0][0]}:00 - {Object.entries(
                      results.reduce((acc, r) => {
                        const hour = new Date(r.timestamp).getHours();
                        acc[hour] = (acc[hour] || 0) + 1;
                        return acc;
                      }, {})
                    )
                    .sort((a, b) => a[1] - b[1])[0][0] + 1}:00</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Recommendations</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  {results.filter(r => r.mealType === 'breakfast').length < results.length * 0.2 && (
                    <li>
                      <span className="text-yellow-600">Consider adding more breakfast meals to your routine.</span>
                    </li>
                  )}
                  {results.filter(r => r.mealType === 'snack').length > results.length * 0.3 && (
                    <li>
                      <span className="text-red-600">Your snack intake is high. Consider reducing between-meal snacks.</span>
                    </li>
                  )}
                  {Math.max(...results.map(r => parseInt(r.portionSize))) > 10 && (
                    <li>
                      <span className="text-red-600">Some portions are quite large. Consider reducing portion sizes for better control.</span>
                    </li>
                  )}
                  {results.reduce((acc, r) => acc + parseInt(r.portionSize), 0) / results.length < 2 && (
                    <li>
                      <span className="text-yellow-600">Your average portion size is small. Ensure you're getting enough nutrition.</span>
                    </li>
                  )}
                  <li>
                    <span className="text-green-600">Maintain a balanced meal distribution throughout the day.</span>
                  </li>
                  <li>
                    <span className="text-gray-600">Keep tracking to monitor your progress and make adjustments as needed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaBook className="mr-2" />
            How to Use
          </h2>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Select the type of meal (Breakfast, Lunch, Dinner, or Snack)</li>
            <li>Enter the name of the food item</li>
            <li>Enter the portion size in your preferred unit</li>
            <li>Click "Add to Tracker" to record your meal</li>
            <li>View your meal distribution in the chart</li>
            <li>Monitor your eating patterns and adjust accordingly</li>
          </ol>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaLightbulb className="mr-2" />
            Features & Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Meal Tracking</h3>
              <p className="text-gray-600">
                Track all your meals throughout the day
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Portion Control</h3>
              <p className="text-gray-600">
                Monitor your portion sizes for better control
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">
                View your meal distribution in a chart
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Pattern Recognition</h3>
              <p className="text-gray-600">
                Identify your eating patterns and habits
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaQuestionCircle className="mr-2" />
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">What is a food tracker?</h3>
              <p className="text-gray-600">
                A food tracker helps you monitor what you eat, when you eat, and how much you eat.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How can tracking help?</h3>
              <p className="text-gray-600">
                Tracking helps identify patterns, control portions, and achieve nutritional goals.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">What should I track?</h3>
              <p className="text-gray-600">
                Track all meals, snacks, and their portion sizes for a complete picture.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How often should I track?</h3>
              <p className="text-gray-600">
                Track every meal and snack throughout the day for best results.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodTrackerCalculator;
